/**
 * orderHistory — device-local order history, reorder, and favorites.
 *
 * History lives in localStorage (no account needed — same model as the cart).
 * Each successful checkout stores a compact snapshot of what was ordered;
 * reordering re-maps those lines onto the LIVE menu so prices, availability,
 * and modifiers are always current (stale/86'd items are skipped, never
 * silently ordered at old prices).
 *
 * Pure helpers (snapshot/reorder/favorites) are exported for unit tests.
 */

const HISTORY_KEY = 'db_order_history_v1';
const CONTACT_KEY = 'db_order_contact_v1';
const MAX_ORDERS = 20;

// ─── Storage ─────────────────────────────────────────────────────────────────

export function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/** Prepend a placed order; keeps the newest MAX_ORDERS. Returns the new list. */
export function saveOrderToHistory(order) {
  const list = [order, ...loadHistory()].slice(0, MAX_ORDERS);
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch { /* quota */ }
  return list;
}

/** Merge `patch` into one stored order (e.g. its last known stage). Returns the new list. */
export function updateOrderInHistory(orderId, patch) {
  const list = loadHistory().map(o => (o.orderId === orderId ? { ...o, ...patch } : o));
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch { /* quota */ }
  return list;
}

export function clearHistory() {
  try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
}

/** Pickup contact (name/phone) remembered for checkout prefill. */
export function loadContact() {
  try { return JSON.parse(localStorage.getItem(CONTACT_KEY)) || {}; } catch { return {}; }
}

export function saveContact({ name, phone }) {
  try {
    localStorage.setItem(CONTACT_KEY, JSON.stringify({
      name: (name || '').slice(0, 80),
      phone: (phone || '').slice(0, 20),
    }));
  } catch { /* ignore */ }
}

// ─── Pure helpers ────────────────────────────────────────────────────────────

/** Compact history snapshot of the cart at checkout time. */
export function snapshotOrder({ cartItems, result, payAtStore, pickupName }) {
  return {
    orderId: result?.orderId || null,
    placedAt: new Date().toISOString(),
    totalCents: result?.totalMoney?.amount ?? null,
    payAtStore: !!payAtStore,
    receiptUrl: result?.receiptUrl || null,
    pickupName: pickupName || '',
    items: (cartItems || []).map(it => ({
      catalogObjectId: it.catalogObjectId || null,
      variationId: it.variationId || null,
      variationName: it.variationName || null,
      name: it.name,
      qty: it.qty,
      unitPrice: Number(it.unitPrice ?? it.basePrice ?? 0),
      modifiers: (it.modifiers || []).map(m => ({ id: m.id || null, name: m.name })),
      note: it.note || '',
    })),
  };
}

/** Flatten menu categories into { byId, byName } lookups of orderable items. */
function indexMenu(categories) {
  const byId = new Map();
  const byName = new Map();
  (categories || []).forEach(c => (c.items || []).forEach(it => {
    if (it.ecomVisible === false) return;
    byId.set(String(it.id), it);
    byName.set(it.name.toLowerCase(), it);
  }));
  return { byId, byName };
}

/**
 * Re-map a past order onto the live menu.
 * Returns { adds: [{ cfg, qty }], skipped: [itemName] } — cfg is ready for
 * cart.addConfigured, always priced from the CURRENT menu.
 */
export function buildReorderCart(order, categories) {
  const { byId, byName } = indexMenu(categories);
  const adds = [];
  const skipped = [];

  (order?.items || []).forEach(line => {
    const item = (line.catalogObjectId && byId.get(String(line.catalogObjectId)))
      || byName.get((line.name || '').toLowerCase());
    if (!item || item.soldOut) { skipped.push(line.name); return; }

    const inStock = (item.variations || []).filter(v => !v.soldOut);
    const variation = inStock.find(v => v.id === line.variationId)
      || inStock.find(v => v.name === line.variationName)
      || inStock[0];
    if (!variation) { skipped.push(line.name); return; }

    // Match old choices against the item's current Square modifiers and
    // manager-defined options (current prices); ones that no longer exist are
    // dropped.
    const squareMods = (item.modifierLists || []).flatMap(ml => ml.modifiers || []);
    const customOpts = (item.optionGroups || []).flatMap(g => g.options || []);
    const modifiers = (line.modifiers || []).map(m => {
      const id = String(m.id || '');
      if (id.startsWith('custom:')) {
        const o = customOpts.find(c => `custom:${c.id}` === id) || customOpts.find(c => c.name === m.name);
        return o ? { id: `custom:${o.id}`, name: o.name, priceCents: o.priceCents || 0, custom: true } : null;
      }
      const sm = squareMods.find(cm => cm.id === m.id) || squareMods.find(cm => cm.name === m.name);
      return sm ? { id: sm.id, name: sm.name, priceCents: sm.priceCents || 0 } : null;
    }).filter(Boolean);

    // A required choice that can't be rebuilt means the line can't be ordered
    // as it was — report it instead of adding something the kitchen can't make.
    const chosen = new Set(modifiers.map(m => m.id));
    const count = (opts, toId) => opts.filter(o => chosen.has(toId(o))).length;
    const unmet = (item.modifierLists || []).some(ml => count(ml.modifiers || [], o => o.id) < (ml.min || 0))
      || (item.optionGroups || []).some(g =>
        count(g.options || [], o => `custom:${o.id}`) < (g.type === 'multi' ? (g.min || 0) : (g.required === true ? 1 : 0)));
    if (unmet) { skipped.push(line.name); return; }

    adds.push({
      cfg: {
        catalogObjectId: item.id,
        variationId: variation.id,
        variationName: variation.name,
        name: item.name,
        squareName: item.name,
        basePrice: (variation.priceCents || 0) / 100,
        modifiers,
        note: line.note || '',
      },
      qty: Math.max(1, Math.min(99, line.qty || 1)),
    });
  });

  return { adds, skipped };
}

/**
 * The device's favorite items: most-ordered across history, still orderable
 * on the live menu. Returns up to `n` of [{ item, count }].
 */
export function favoriteItems(history, categories, n = 8) {
  const { byId, byName } = indexMenu(categories);
  const counts = new Map(); // key → { count, catalogObjectId, name }
  (history || []).forEach(o => (o.items || []).forEach(line => {
    const key = String(line.catalogObjectId || line.name || '');
    if (!key) return;
    const cur = counts.get(key) || { count: 0, catalogObjectId: line.catalogObjectId, name: line.name };
    cur.count += line.qty || 1;
    counts.set(key, cur);
  }));

  return [...counts.values()]
    .map(c => {
      const item = (c.catalogObjectId && byId.get(String(c.catalogObjectId)))
        || byName.get((c.name || '').toLowerCase());
      return item && !item.soldOut ? { item, count: c.count } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

/**
 * Cross-sell suggestions for the cart drawer: up to `n` in-stock items from
 * "rounding out the meal" categories (drinks/desserts/sides) that aren't in
 * the cart yet. Favorites win ties so suggestions feel personal.
 */
export function cartSuggestions(cartItems, categories, history, n = 3) {
  const inCart = new Set((cartItems || []).map(i => String(i.catalogObjectId || i.name)));
  const favs = new Map(favoriteItems(history, categories, 50).map(f => [String(f.item.id), f.count]));
  const WANT = /beverage|drink|dessert|sweet|extra|side/i;

  const pool = (categories || [])
    .filter(c => WANT.test(c.name || ''))
    .flatMap(c => (c.items || []).filter(it =>
      it.ecomVisible !== false && !it.soldOut &&
      !inCart.has(String(it.id)) && !inCart.has(String(it.name))
    ));

  return pool
    .sort((a, b) => (favs.get(String(b.id)) || 0) - (favs.get(String(a.id)) || 0))
    .slice(0, n);
}

/** True when the item needs the configurator (choices) before adding. */
export function needsConfiguration(item) {
  const multiVariation = (item.variations || []).filter(v => !v.soldOut).length > 1;
  const requiredMods = (item.modifierLists || []).some(ml => (ml.min || 0) > 0);
  // Any manager-defined option group (dosa type, add-ons) means the customer
  // should see the configurator rather than a silent one-tap add.
  const hasOptionGroups = (item.optionGroups || []).length > 0;
  return multiVariation || requiredMods || hasOptionGroups;
}

/** addConfigured-ready cfg for a menu item's default (first in-stock) variation. */
export function defaultConfig(item) {
  const v = (item.variations || []).find(x => !x.soldOut) || (item.variations || [])[0];
  if (!v) return null;
  return {
    catalogObjectId: item.id,
    variationId: v.id,
    variationName: v.name,
    name: item.name,
    squareName: item.name,
    basePrice: (v.priceCents || 0) / 100,
    modifiers: [],
    note: '',
  };
}
